const maxCachedImages = 150;

interface CachedImage {
  lastUsed: number;
  objectUrl: string;
}

interface PendingImage {
  lastUsed: number;
  promise: Promise<string>;
}

type ImageCacheEntry = CachedImage | PendingImage;

const imageCache = new Map<string, ImageCacheEntry>();

export function getCachedImageUrl(sourceUrl: string) {
  const cachedImage = imageCache.get(sourceUrl);

  if (!cachedImage || !('objectUrl' in cachedImage)) {
    return null;
  }

  cachedImage.lastUsed = Date.now();
  return cachedImage.objectUrl;
}

export function loadCachedImage(sourceUrl: string) {
  const cachedImage = imageCache.get(sourceUrl);

  if (cachedImage) {
    cachedImage.lastUsed = Date.now();

    if ('objectUrl' in cachedImage) {
      return Promise.resolve(cachedImage.objectUrl);
    }

    return cachedImage.promise;
  }

  const pendingImage: PendingImage = {
    lastUsed: Date.now(),
    promise: fetch(sourceUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Image request failed with status ${response.status}`);
        }

        return response.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const cachedImage: CachedImage = {
          lastUsed: Date.now(),
          objectUrl,
        };

        imageCache.set(sourceUrl, cachedImage);
        pruneImageCache();

        return objectUrl;
      })
      .catch((error) => {
        imageCache.delete(sourceUrl);
        throw error;
      }),
  };

  imageCache.set(sourceUrl, pendingImage);

  return pendingImage.promise;
}

export function removeCachedImage(sourceUrl: string) {
  const cachedImage = imageCache.get(sourceUrl);

  if (cachedImage && 'objectUrl' in cachedImage) {
    URL.revokeObjectURL(cachedImage.objectUrl);
  }

  imageCache.delete(sourceUrl);
}

export function clearImageCache() {
  imageCache.forEach((cachedImage) => {
    if ('objectUrl' in cachedImage) {
      URL.revokeObjectURL(cachedImage.objectUrl);
    }
  });
  imageCache.clear();
}

function pruneImageCache() {
  if (imageCache.size <= maxCachedImages) {
    return;
  }

  const cachedImages = Array.from(imageCache.entries())
    .filter((entry): entry is [string, CachedImage] => 'objectUrl' in entry[1])
    .sort(([, left], [, right]) => left.lastUsed - right.lastUsed);

  const removeCount = imageCache.size - maxCachedImages;

  cachedImages.slice(0, removeCount).forEach(([sourceUrl]) => removeCachedImage(sourceUrl));
}
